import React, { useState, useRef } from 'react';
import { BarcodeScanner, Barcode, BarcodeFormat } from '@capacitor-mlkit/barcode-scanning';
import { Capacitor } from '@capacitor/core';
import { BrowserMultiFormatReader } from '@zxing/library';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';
import { BarcodeResultModal } from './BarcodeResultModal';
import { lookupLocation } from '../services/locationService';
import { Product } from '../services/productService';
import { Scan, Upload } from 'lucide-react';
import '../styles/components/codeScanner.css';

interface CodeScannerProps {
  token: string;
  onProductScanned?: (product: Product) => void;
  onLocationScanned?: (location: {
    area: string;
    location: string;
    sub_location: string;
  }) => void;
  onShowToast?: (message: string) => void;
  buttonText?: string;
  buttonClassName?: string;
  hasShoppingCollection?: boolean;
}

type ScanType = 'barcode' | 'qr_location' | 'unknown';

/**
 * Code Scanner - Auto-detects barcode/QR code type and routes accordingly
 * 
 * Supports:
 * - Product barcodes (UPC, EAN, etc.)
 * - Location QR codes (Wandering Sock format)
 * - Generic QR codes
 * - File upload (desktop and mobile Photos)
 */
export const CodeScanner: React.FC<CodeScannerProps> = ({
  token,
  onProductScanned,
  onLocationScanned,
  onShowToast,
  buttonText = 'Scan',
  buttonClassName = 'code-scan-button',
  hasShoppingCollection = false
}) => {
  const { scanSingle, isScanning } = useBarcodeScanner();
  const [scannedBarcode, setScannedBarcode] = useState<Barcode | null>(null);
  const [scanType, setScanType] = useState<ScanType | null>(null);
  const [locationData, setLocationData] = useState<any>(null);
  const [showScanner, setShowScanner] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [nativeScannerSupported, setNativeScannerSupported] = useState<boolean | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isNativePlatform = Capacitor.isNativePlatform();

  // Check if native barcode scanner is supported (real device vs simulator)
  React.useEffect(() => {
    const checkNativeSupport = async () => {
      if (!isNativePlatform) {
        setNativeScannerSupported(false);
        return;
      }
      
      try {
        const { supported } = await BarcodeScanner.isSupported();
        console.log('[CodeScanner] Native scanner supported:', supported);
        setNativeScannerSupported(supported);
      } catch (error) {
        console.log('[CodeScanner] Native scanner check failed, using file picker:', error);
        setNativeScannerSupported(false);
      }
    };
    
    checkNativeSupport();
  }, [isNativePlatform]);

  const detectScanType = (barcode: Barcode): ScanType => {
    console.log('[CodeScanner] Detecting scan type for format:', barcode.format, 'value:', barcode.rawValue);
    
    // QR Code - check if it's a product barcode or location QR
    if (barcode.format === BarcodeFormat.QrCode) {
      const value = barcode.rawValue || '';
      
      // If QR contains only digits (8-14 chars), it's likely a product barcode misidentified by ZXing
      if (/^\d{8,14}$/.test(value)) {
        console.log('[CodeScanner] QR code contains only digits, treating as product barcode');
        return 'barcode';
      }
      
      // All other QR codes are treated as location QR codes (pre-printed labels)
      console.log('[CodeScanner] QR code detected, treating as location identifier');
      return 'qr_location';
    }

    // All other barcode formats are treated as product barcodes
    // This includes: UPC-A, UPC-E, EAN-13, EAN-8, Code 39, Code 93, Code 128, ITF, Codabar, etc.
    return 'barcode';
  };

  const handleScan = async () => {
    console.log('[CodeScanner] handleScan called, isNativePlatform:', isNativePlatform);
    setShowScanner(true);

    try {
      console.log('[CodeScanner] Calling scanSingle...');
      const result = await scanSingle();
      console.log('[CodeScanner] scanSingle result:', result);

      if (result.success && result.barcode) {
        const type = detectScanType(result.barcode);
        console.log('[CodeScanner] Detected scan type:', type);
        console.log('[CodeScanner] Barcode details:', {
          rawValue: result.barcode.rawValue,
          displayValue: result.barcode.displayValue,
          format: result.barcode.format,
          valueType: result.barcode.valueType
        });

        setScanType(type);

        if (type === 'qr_location') {
          // Handle location QR code
          await handleLocationQR(result.barcode);
        } else if (type === 'barcode') {
          // Handle product barcode
          console.log('[CodeScanner] Setting scanned barcode:', result.barcode.rawValue);
          setScannedBarcode(result.barcode);
        } else {
          // Unknown type
          alert('Unrecognized QR code or barcode format');
          setShowScanner(false);
        }
      } else {
        console.log('[CodeScanner] Scan failed or cancelled:', result.error);
        if (result.error) {
          alert(`Scan failed: ${result.error}`);
        }
        setShowScanner(false);
      }
    } catch (error) {
      console.error('[CodeScanner] handleScan error:', error);
      alert(`Scan error: ${error}`);
      setShowScanner(false);
    }
  };

  const handleLocationQR = async (barcode: Barcode) => {
    const qrValue = barcode.rawValue || '';
    
    console.log('[CodeScanner] Location QR scanned:', qrValue);
    
    // For pre-printed QR labels, just pass the raw value to the callback
    // The parent component will handle looking up items with this sub_location
    if (onLocationScanned) {
      onLocationScanned({
        area: '',
        location: '',
        sub_location: qrValue
      });
    }
    
    setShowScanner(false);
    setScanType(null);
  };

  const handleProductAdded = (product: Product) => {
    setScannedBarcode(null);
    setScanType(null);
    setShowScanner(false);

    if (onProductScanned) {
      onProductScanned(product);
    }

    // Show success toast (self-closing)
    if (onShowToast) {
      onShowToast(`✅ Added ${product.name}`);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log('[CodeScanner] File selected:', file.name, file.type, file.size);
    setIsProcessing(true);
    setShowScanner(true);

    try {
      // Create image element from file
      console.log('[CodeScanner] Creating image URL...');
      const imageUrl = URL.createObjectURL(file);
      const img = new Image();
      
      console.log('[CodeScanner] Loading image...');
      await new Promise((resolve, reject) => {
        img.onload = () => {
          console.log('[CodeScanner] Image loaded:', img.width, 'x', img.height);
          resolve(null);
        };
        img.onerror = (e) => {
          console.error('[CodeScanner] Image load error:', e);
          reject(new Error('Failed to load image'));
        };
        img.src = imageUrl;
      });

      // Preprocess image to improve barcode detection
      console.log('[CodeScanner] Preprocessing image for better detection...');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      // Set canvas size
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw image
      ctx.drawImage(img, 0, 0);
      
      // Enhance contrast
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Simple contrast enhancement
      const factor = 1.5; // Increase contrast
      for (let i = 0; i < data.length; i += 4) {
        data[i] = Math.min(255, ((data[i] - 128) * factor) + 128);     // R
        data[i + 1] = Math.min(255, ((data[i + 1] - 128) * factor) + 128); // G
        data[i + 2] = Math.min(255, ((data[i + 2] - 128) * factor) + 128); // B
      }
      
      ctx.putImageData(imageData, 0, 0);
      
      // Convert canvas to image for ZXing
      const processedImg = new Image();
      const processedUrl = canvas.toDataURL();
      
      await new Promise((resolve, reject) => {
        processedImg.onload = resolve;
        processedImg.onerror = reject;
        processedImg.src = processedUrl;
      });

      // Try to decode barcode/QR from processed image with multiple attempts
      let result;
      const codeReader = new BrowserMultiFormatReader();
      
      // Attempt 1: Original processed image
      try {
        console.log('[CodeScanner] Attempt 1: Decoding original image...');
        result = await codeReader.decodeFromImageElement(processedImg);
        console.log('[CodeScanner] Success on attempt 1');
      } catch (e1) {
        console.log('[CodeScanner] Attempt 1 failed, trying rotations...');
        
        // Attempt 2-4: Try with slight rotations to handle angled barcodes
        const rotations = [-5, 5, -10]; // degrees
        for (const angle of rotations) {
          try {
            const rotatedCanvas = document.createElement('canvas');
            const rotatedCtx = rotatedCanvas.getContext('2d');
            if (!rotatedCtx) continue;
            
            // Calculate rotated dimensions
            const radians = (angle * Math.PI) / 180;
            const cos = Math.abs(Math.cos(radians));
            const sin = Math.abs(Math.sin(radians));
            rotatedCanvas.width = processedImg.width * cos + processedImg.height * sin;
            rotatedCanvas.height = processedImg.width * sin + processedImg.height * cos;
            
            // Rotate and draw
            rotatedCtx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
            rotatedCtx.rotate(radians);
            rotatedCtx.drawImage(processedImg, -processedImg.width / 2, -processedImg.height / 2);
            
            // Convert canvas to image
            const rotatedImg = new Image();
            const rotatedUrl = rotatedCanvas.toDataURL();
            await new Promise((resolve, reject) => {
              rotatedImg.onload = resolve;
              rotatedImg.onerror = reject;
              rotatedImg.src = rotatedUrl;
            });
            
            // Try to decode rotated image
            result = await codeReader.decodeFromImageElement(rotatedImg);
            console.log(`[CodeScanner] Success with ${angle}° rotation`);
            break;
          } catch (e2) {
            // Continue to next rotation
          }
        }
        
        if (!result) {
          console.log('[CodeScanner] No barcode/QR code detected');
          URL.revokeObjectURL(imageUrl);
          alert('No barcode or QR code found in image.\n\nTips:\n- Ensure the barcode/QR is clearly visible\n- Try taking photo straight-on\n- Use the Receipt tab for receipt scanning');
          setShowScanner(false);
          return;
        }
      }
      
      console.log('[CodeScanner] ZXing decode result:', result);
      
      URL.revokeObjectURL(imageUrl);

      if (result && result.getText()) {
        console.log('[CodeScanner] Decoded barcode from image:', result.getText());
        
        // Convert ZXing result to Capacitor Barcode format
        const barcode: Barcode = {
          rawValue: result.getText(),
          displayValue: result.getText(),
          format: mapZXingFormatToCapacitor(result.getBarcodeFormat()),
          valueType: 'TEXT' as any,
          cornerPoints: [[0, 0], [0, 0], [0, 0], [0, 0]],
          bytes: []
        };

        const type = detectScanType(barcode);
        console.log('[CodeScanner] Detected scan type from upload:', type);

        setScanType(type);

        if (type === 'qr_location') {
          await handleLocationQR(barcode);
        } else if (type === 'barcode') {
          setScannedBarcode(barcode);
        } else {
          alert('Unrecognized QR code or barcode format');
          setShowScanner(false);
        }
      } else {
        alert('No barcode or QR code found in image.\n\nTips:\n- Ensure the barcode/QR is clearly visible\n- Try taking photo straight-on\n- Use the Receipt tab for receipt scanning');
        setShowScanner(false);
      }
    } catch (error: any) {
      console.error('[CodeScanner] Failed to process image:', error);
      alert(`Failed to process image: ${error.message || 'Unknown error'}`);
      setShowScanner(false);
    } finally {
      setIsProcessing(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const mapZXingFormatToCapacitor = (zxingFormat: any): BarcodeFormat => {
    const formatMap: { [key: string]: BarcodeFormat } = {
      'QR_CODE': BarcodeFormat.QrCode,
      'UPC_A': BarcodeFormat.UpcA,
      'UPC_E': BarcodeFormat.UpcE,
      'EAN_13': BarcodeFormat.Ean13,
      'EAN_8': BarcodeFormat.Ean8,
      'CODE_128': BarcodeFormat.Code128,
      'CODE_39': BarcodeFormat.Code39,
      'CODE_93': BarcodeFormat.Code93,
    };
    return formatMap[zxingFormat.toString()] || BarcodeFormat.QrCode;
  };

  const handleCancel = () => {
    setScannedBarcode(null);
    setScanType(null);
    setShowScanner(false);
    setLocationData(null);
  };

  return (
    <>
      {/* Single wide button matching Receipt page layout */}
      <button
        className={buttonClassName}
        onClick={() => {
          console.log('[CodeScanner] Button clicked, isNativePlatform:', isNativePlatform, 'nativeScannerSupported:', nativeScannerSupported, 'isScanning:', isScanning, 'isProcessing:', isProcessing);
          
          // Use native camera scanner if supported (real iOS device)
          // Otherwise use file picker (web, iOS simulator)
          if (nativeScannerSupported) {
            console.log('[CodeScanner] Using native camera scanner');
            handleScan();
          } else {
            console.log('[CodeScanner] Using file picker (web or iOS simulator)');
            fileInputRef.current?.click();
          }
        }}
        disabled={isScanning || isProcessing}
      >
        {isProcessing ? (
          <>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid rgba(255, 255, 255, 0.3)',
              borderTop: '4px solid currentColor',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <span>Processing barcode...</span>
            <span className="add-item-scan-hint">Decoding image, please wait</span>
          </>
        ) : (
          <>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 5v14M7 5v14M10 5v14M13 5v14M16 5v14M19 5v14M21 5v14"></path>
            </svg>
            <span>{buttonText}</span>
            <span className="add-item-scan-hint">Add items by barcode or find items by QR code</span>
          </>
        )}
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />

      {/* Show barcode result modal for product barcodes */}
      {scannedBarcode && scanType === 'barcode' && (
        <BarcodeResultModal
          barcode={scannedBarcode}
          token={token}
          onAddToInventory={handleProductAdded}
          onCancel={handleCancel}
          hasShoppingCollection={hasShoppingCollection}
        />
      )}
    </>
  );
};
